import dayjs from "dayjs";
import moment from "moment";
import { isSameAppointmentRow } from "@helpers/mergeAppointmentWithMember";

/** 後端諮詢師名「不指定」等 → 顯示「不指定諮詢師」 */
function formatCounsellorDisplayName(name) {
  const normalized = String(name ?? "").trim();
  if (!normalized) return "";
  if (
    normalized === "不指定" ||
    normalized === "不指定諮詢師" ||
    normalized === "不指定咨詢師"
  ) {
    return "不指定諮詢師";
  }
  return normalized;
}

/** 將分店服務列表轉成 id → 服務物件的查詢表 */
export function buildServicesCatalog(branchServices = []) {
  if (!Array.isArray(branchServices)) return {};
  return branchServices.reduce((acc, service) => {
    if (service?.id != null) {
      acc[service.id] = service;
    }
    return acc;
  }, {});
}

/** 將單筆服務（物件或純 id）正規化成 { service_id, service_name } */
export function normalizeAppointmentServiceEntry(entry, servicesCatalog = {}) {
  if (entry == null || entry === "") return null;

  if (typeof entry === "number" || typeof entry === "string") {
    const id = Number(entry);
    if (!Number.isFinite(id)) return null;
    const catalog = servicesCatalog[id];
    return {
      service_id: id,
      id,
      service_name: catalog?.service_name || catalog?.name || "",
    };
  }

  if (typeof entry !== "object") return null;

  const serviceId = entry.service_id ?? entry.id ?? entry.serviceId;
  const catalog =
    serviceId != null ? servicesCatalog[serviceId] : undefined;

  return {
    ...entry,
    service_id: serviceId,
    service_name:
      entry.service_name ||
      entry.name ||
      catalog?.service_name ||
      catalog?.name ||
      "",
  };
}

/**
 * 解析預約上的服務列表（相容多種 API 格式）。
 * - services 可能為物件陣列、純 id 陣列，或缺漏
 * - 可從月曆／列表資料（calendarRows）依預約 id 補齊
 */
export function resolveAppointmentServices(appointment, options = {}) {
  if (!appointment) return [];

  const {
    branchServices = [],
    servicesCatalog: catalogInput,
    calendarRows = [],
  } = options;

  const servicesCatalog =
    catalogInput || buildServicesCatalog(branchServices);

  const normalizeList = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .map((entry) => normalizeAppointmentServiceEntry(entry, servicesCatalog))
      .filter((s) => s?.service_id != null);
  };

  let resolved = normalizeList(appointment.services);

  if (resolved.length === 0) {
    resolved = normalizeList(
      appointment.service_list || appointment.appointment_services
    );
  }

  if (resolved.length === 0 && calendarRows.length > 0) {
    const matched = calendarRows.find((row) =>
      isSameAppointmentRow(row, appointment)
    );
    if (matched) {
      resolved = normalizeList(matched.services);
      if (resolved.length === 0) {
        resolved = normalizeList(
          matched.service_list || matched.appointment_services
        );
      }
    }
  }

  return resolved;
}

/** 從預約解析可填入表單的 service id 陣列 */
export function getAppointmentServiceIds(appointment, options = {}) {
  return resolveAppointmentServices(appointment, options)
    .map((s) => s.service_id)
    .filter((id) => id != null && id !== "");
}

function normalizeEmployeeId(value) {
  if (value == null || value === "") return null;
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

/** 從月曆／分店預約列表補齊諮詢師（getAppointmentsByBrandUser 常缺此欄位） */
export function getCounsellorFieldsFromCalendarRow(appointment, calendarRows = []) {
  if (!appointment || !Array.isArray(calendarRows) || calendarRows.length === 0) {
    return null;
  }

  const matched = calendarRows.find((row) =>
    isSameAppointmentRow(row, appointment)
  );
  if (!matched) return null;

  const counsellor_id = matched.counsellor_id ?? matched.counsellorId ?? null;
  const counsellor_name =
    matched.counsellor_name ?? matched.counsellorName ?? "";

  if (counsellor_id == null && !counsellor_name) return null;

  return { counsellor_id, counsellorId: counsellor_id, counsellor_name, counsellorName: counsellor_name };
}

function getAppointmentCounsellorFieldSources(appointment, options = {}) {
  const calendarFields = getCounsellorFieldsFromCalendarRow(
    appointment,
    options.calendarRows
  );

  return {
    counsellor_id:
      appointment?.counsellor_id ??
      appointment?.counsellorId ??
      calendarFields?.counsellor_id ??
      calendarFields?.counsellorId ??
      null,
    counsellor_name: (
      appointment?.counsellor_name ??
      appointment?.counsellorName ??
      calendarFields?.counsellor_name ??
      calendarFields?.counsellorName ??
      ""
    ).trim(),
  };
}

function getCounsellorNameFromCatalog(id, options = {}) {
  if (id == null) return "";
  const { employeesObj, employees = [], consultantsOptions = [] } = options;

  const fromObj =
    employeesObj?.[id]?.name || employeesObj?.[String(id)]?.name;
  if (fromObj) return fromObj;

  const fromEmployees = employees.find((e) => Number(e.id) === Number(id));
  if (fromEmployees?.name) return fromEmployees.name;

  const fromOption = consultantsOptions.find(
    (o) => Number(o.value ?? o.id) === Number(id)
  );
  return (fromOption?.label || fromOption?.name || "").trim();
}

/** 解析預約諮詢師 ID（相容 counsellor_id / counsellorId、月曆補齊、或以姓名反查） */
export function resolveAppointmentCounsellorId(appointment, options = {}) {
  if (!appointment) return null;

  const { counsellor_id: rawId, counsellor_name: rawName } =
    getAppointmentCounsellorFieldSources(appointment, options);

  const fromField = normalizeEmployeeId(rawId);
  if (fromField != null) return fromField;

  if (!rawName) return null;

  const { employeesObj, employees = [], consultantsOptions = [] } = options;
  const employeeList = [
    ...(employeesObj ? Object.values(employeesObj) : []),
    ...employees,
  ];

  const matchedEmployee = employeeList.find(
    (e) => e?.job_title === "COUNSELLOR" && e?.name === rawName
  );
  const fromEmployee = normalizeEmployeeId(matchedEmployee?.id);
  if (fromEmployee != null) return fromEmployee;

  const matchedOption = consultantsOptions.find(
    (o) => (o.label || o.name || "").trim() === rawName
  );
  return normalizeEmployeeId(matchedOption?.value ?? matchedOption?.id);
}

/** 解析預約諮詢師姓名（優先 API／月曆欄位，其次員工表；系統不指定顯示為「不指定諮詢師」） */
export function resolveAppointmentCounsellorName(appointment, options = {}) {
  if (!appointment) return "";

  const { counsellor_name: directName } = getAppointmentCounsellorFieldSources(
    appointment,
    options
  );
  if (directName) return formatCounsellorDisplayName(directName);

  const counsellorId = resolveAppointmentCounsellorId(appointment, options);
  return formatCounsellorDisplayName(
    getCounsellorNameFromCatalog(counsellorId, options)
  );
}

/** 將 API／表單的預約類別正規化為多選陣列（FOLLOW_UP 等 enum） */
export function normalizeAppointmentType(raw) {
  if (raw == null || raw === "") return [];

  let value = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        value = Array.isArray(parsed) ? parsed : [trimmed];
      } catch(err) {
        console.log("Error parsing appointment type", err);
        value = [trimmed];
      }
    } else if (trimmed.includes(",")) {
      value = trimmed
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    } else {
      value = [trimmed];
    }
  }

  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => (typeof entry === "string" ? entry.trim() : entry))
    .filter((entry) => entry != null && entry !== "");
}

/** 從月曆／分店預約列表補齊預約類別（getAppointmentsByBrandUser 常缺此欄位） */
export function getAppointmentTypeFromCalendarRow(appointment, calendarRows = []) {
  if (!appointment || !Array.isArray(calendarRows) || calendarRows.length === 0) {
    return null;
  }

  const matched = calendarRows.find((row) =>
    isSameAppointmentRow(row, appointment)
  );
  if (!matched) return null;

  const raw = matched.appointment_type ?? matched.appointmentType;
  if (raw == null || raw === "") return null;
  return raw;
}

/** 解析預約類別（相容字串／陣列，並可從月曆列補齊） */
export function resolveAppointmentType(appointment, options = {}) {
  if (!appointment) return [];

  const raw =
    appointment.appointment_type ??
    appointment.appointmentType ??
    getAppointmentTypeFromCalendarRow(appointment, options.calendarRows);

  return normalizeAppointmentType(raw);
}

/** 正規化預約上的預約類別欄位（供側欄列表、預覽、複製共用） */
export function enrichAppointmentTypeFields(appointment, options = {}) {
  if (!appointment) return appointment;

  const appointment_type = resolveAppointmentType(appointment, options);
  if (appointment_type.length === 0) return appointment;

  return {
    ...appointment,
    appointment_type,
    appointmentType: appointment_type,
  };
}

/** 正規化預約上的諮詢師欄位（供側欄列表、預覽、複製共用） */
export function enrichAppointmentCounsellorFields(appointment, options = {}) {
  if (!appointment) return appointment;

  const counsellor_id = resolveAppointmentCounsellorId(appointment, options);
  const counsellor_name = resolveAppointmentCounsellorName(
    counsellor_id != null
      ? { ...appointment, counsellor_id, counsellorId: counsellor_id }
      : appointment,
    options
  );

  return {
    ...appointment,
    ...(counsellor_id != null
      ? { counsellor_id, counsellorId: counsellor_id }
      : {}),
    ...(counsellor_name
      ? { counsellor_name, counsellorName: counsellor_name }
      : {}),
  };
}

/** 從月曆／分店預約列表補齊 is_first_visit（getAppointmentsByBrandUser 常缺此欄位） */
export function getIsFirstVisitFromCalendarRow(appointment, calendarRows = []) {
  if (!appointment || !Array.isArray(calendarRows) || calendarRows.length === 0) {
    return null;
  }

  const matched = calendarRows.find((row) =>
    isSameAppointmentRow(row, appointment)
  );
  if (!matched) return null;
  if (matched.is_first_visit === true || matched.is_first_visit === false) {
    return matched.is_first_visit;
  }
  return null;
}

/** 正規化預約上的 is_first_visit（供側欄列表、預覽、複製共用） */
export function enrichAppointmentIsFirstVisitFields(appointment, options = {}) {
  if (!appointment) return appointment;
  if (
    appointment.is_first_visit === true ||
    appointment.is_first_visit === false
  ) {
    return appointment;
  }

  const fromCalendar = getIsFirstVisitFromCalendarRow(
    appointment,
    options.calendarRows
  );
  if (fromCalendar === true || fromCalendar === false) {
    return { ...appointment, is_first_visit: fromCalendar };
  }

  return appointment;
}

/** 將解析後的服務寫回預約物件（供列表／彈窗顯示與複製共用） */
export function enrichAppointmentWithServices(appointment, options = {}) {
  if (!appointment) return appointment;

  const withServices = {
    ...appointment,
    services: resolveAppointmentServices(appointment, options),
  };

  return enrichAppointmentIsFirstVisitFields(
    enrichAppointmentTypeFields(
      enrichAppointmentCounsellorFields(withServices, options),
      options
    ),
    options
  );
}

/**
 * 合併會員側欄 API 預約與月曆 state（補齊 is_first_visit，並納入尚未出現在 API 的新增預約）。
 */
export function mergeMemberAppointmentsWithCalendarRows(
  userId,
  apiAppointments = [],
  calendarRows = [],
  options = {}
) {
  const mergeOptions = { ...options, calendarRows };

  if (userId == null || userId === "") {
    return (apiAppointments || []).map((a) =>
      enrichAppointmentWithServices(a, mergeOptions)
    );
  }

  const uid = String(userId);
  const calendarRowsForUser = (calendarRows || []).filter(
    (row) =>
      String(row.user_id) === uid && row.appointment_status_id !== 2
  );

  const enrichedFromApi = (apiAppointments || []).map((a) =>
    enrichAppointmentWithServices(a, mergeOptions)
  );

  const extras = calendarRowsForUser
    .filter(
      (row) => !enrichedFromApi.some((a) => isSameAppointmentRow(a, row))
    )
    .map((row) => enrichAppointmentWithServices(row, mergeOptions));

  return [...enrichedFromApi, ...extras];
}

/**
 * 會員建立時間欄位（依常見 API 命名；不含 updated_at，避免編輯資料後誤判）。
 */
export function extractMemberCreatedAt(member) {
  if (!member || typeof member !== "object") return null;

  const raw =
    member.created_at ??
    member.createdAt ??
    member.member_created_at ??
    member.memberCreatedAt ??
    member.registered_at ??
    member.registeredAt ??
    member._clientMemberCreatedAt ??
    null;

  if (raw == null || raw === "") return null;

  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
}

/** 前端新建會員且 API 未回傳建立時間時，補 client 時間戳（僅供新增預約 optimistic fallback 使用）。 */
export function stampMemberCreatedAtIfMissing(member) {
  if (!member || typeof member !== "object") return member;
  if (extractMemberCreatedAt(member)) return member;
  return { ...member, _clientMemberCreatedAt: new Date().toISOString() };
}

export function findMemberByUserId(members, userId) {
  if (userId == null || userId === "" || !Array.isArray(members)) return null;
  const uid = String(userId);
  return (
    members.find(
      (m) => String(m.id) === uid || String(m.user_id) === uid
    ) ?? null
  );
}

export const RECENTLY_CREATED_MEMBER_HOURS = 1;

/** 會員建立時間是否在指定小時內（僅供新增預約 optimistic fallback）。 */
export function isMemberCreatedWithinHours(
  member,
  hours = RECENTLY_CREATED_MEMBER_HOURS,
  now = dayjs()
) {
  const createdAt = extractMemberCreatedAt(member);
  if (!createdAt) return false;

  const diffMs = now.valueOf() - createdAt.valueOf();
  return diffMs >= 0 && diffMs <= hours * 60 * 60 * 1000;
}

/**
 * 預約寫回 state 時解析 is_first_visit（optimistic 輔助）。
 * 優先順序：表單明確值 → API 回傳 → 既有 state →（僅新增預約且 API 無資料時）會員建立 1 小時內。
 *
 * @param {boolean} [optimisticRecentMemberFallback=false]
 *   僅在「新增預約後、API 未回 is_first_visit、尚未重整」時設為 true。
 */
export function resolveNewAppointmentIsFirstVisit({
  apiData,
  existingIsFirstVisit,
  explicitValue,
  member,
  optimisticRecentMemberFallback = false,
} = {}) {
  if (explicitValue === true || explicitValue === false) {
    return explicitValue;
  }

  const fromApi = apiData?.is_first_visit ?? apiData?.isFirstVisit;
  if (fromApi === true || fromApi === false) {
    return fromApi;
  }

  if (existingIsFirstVisit === true || existingIsFirstVisit === false) {
    return existingIsFirstVisit;
  }

  if (
    optimisticRecentMemberFallback &&
    isMemberCreatedWithinHours(member)
  ) {
    return true;
  }

  return undefined;
}

/** 將 getAppointmentsByBrandUser 單筆資料轉成含 start/end 的預約物件 */
export function mapBrandUserAppointmentRecord(v) {
  const dateTimeAddition = (initialDateTime, additionalTime) => {
    const originalDateTime = moment(initialDateTime);
    const originalTime = moment(additionalTime, "HH:mm:ss");
    return new Date(
      originalDateTime.clone().add({
        hours: originalTime.hours() - 8,
        minutes: originalTime.minutes(),
        seconds: originalTime.seconds(),
      })
    );
  };

  return {
    ...v,
    appointment_id: v.appointment_id ?? v.id,
    start: dateTimeAddition(v.appointment_date, v.start_time),
    end: dateTimeAddition(v.appointment_date, v.end_time),
  };
}

/** 可複製的預約：排除已取消，依日期新到舊排序；可選限當前分店 */
export function filterCopyableAppointments(appointments, branchId) {
  return appointments
    .filter((v) => {
      if (v.appointment_status_id === 2) return false;
      if (branchId != null && v.branch_id != null && v.branch_id !== branchId) {
        return false;
      }
      return true;
    })
    .sort(
      (a, b) =>
        moment(b.appointment_date).unix() - moment(a.appointment_date).unix()
    );
}

/** 格式化預約列表顯示用日期 */
export function formatAppointmentListDate(appointmentDate) {
  const formattedDate = dayjs(appointmentDate, "YYYY-MM-DD", true).isValid()
    ? dayjs(appointmentDate, "YYYY-MM-DD")
    : dayjs(appointmentDate);
  return {
    selectedYear: formattedDate.format("YYYY"),
    selectedMonth: formattedDate.format("M月"),
    selectedDay: formattedDate.format("D日"),
    weekDay: formattedDate.locale("zh-tw").format("dddd"),
    displayDate: formattedDate.format("YYYY-MM-DD"),
  };
}

/** 從過往預約擷取可填入新增表單的欄位（不含日期） */
export function getAppointmentCopyFormValues(appointment, options = {}) {
  const resolvedAppointment = enrichAppointmentWithServices(
    appointment,
    options
  );
  const serviceIds = getAppointmentServiceIds(resolvedAppointment, options);

  const appointmentType = resolveAppointmentType(resolvedAppointment, options);

  const employee = appointment.employee_id ?? null;

  const startTimeRaw = appointment.start_time;
  const endTimeRaw = appointment.end_time;

  return {
    serviceIds,
    employee,
    isUnassigned: appointment.is_unassigned === true ? 1 : 0,
    counsellorId: resolveAppointmentCounsellorId(appointment, options),
    appointmentType,
    branchNote: appointment.branch_note ?? "",
    isPaidConsultation: appointment.is_paid_consultation ?? null,
    paidConsultation: appointment.paid_consultation ?? 0,
    startTimeRaw,
    endTimeRaw,
    sourceLabel: formatAppointmentCopySourceLabel(
      resolvedAppointment,
      options
    ),
  };
}

export function formatAppointmentCopySourceLabel(appointment, options = {}) {
  const { displayDate } = formatAppointmentListDate(appointment.appointment_date);
  const start = appointment.start_time
    ? dayjs(`${displayDate}T${appointment.start_time}`).format("HH:mm")
    : "";
  const end = appointment.end_time
    ? dayjs(`${displayDate}T${appointment.end_time}`).format("HH:mm")
    : "";
  const services = resolveAppointmentServices(appointment, options)
    .map((s) => s.service_name)
    .filter(Boolean)
    .join("、");
  const timePart = start && end ? `${start}–${end}` : start || "";
  return [displayDate, timePart, services].filter(Boolean).join(" · ");
}

/** 將日期與 HH:mm:ss 合併為表單用的 ISO 字串 */
export function mergeAppointmentDateAndTime(dateStr, timeRaw) {
  if (!dateStr || !timeRaw) return null;
  const displayDate = dayjs(dateStr).format("YYYY-MM-DD");
  return dayjs(`${displayDate}T${timeRaw}`).format("YYYY-MM-DDTHH:mm:ss");
}
